import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getTrialBalance,
  clearTrialBalance,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { convertArabicNumbersToEnglish, calculatePdfPagination } from '../../../../shared/utils.js/utils';
import PdfPreviewModal from './components/PdfPreviewModal';
import {
  generatePdfFromElementIds,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';

const TrialBalance = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;
  const {
    trialBalance,
    trialBalanceFilters,
    isLoading,
    error,
    success,
  } = useSelector((state) => state.auth);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const pdfContainerRef = useRef(null);

  const initialFiltersRef = useRef(trialBalanceFilters);

  useEffect(() => {
    setTitle('sidenav.trialBalance');
  }, [setTitle]);

  useEffect(() => {
    if (initialFiltersRef.current) {
      dispatch(getTrialBalance(initialFiltersRef.current));
    }
    return () => {
      dispatch(clearTrialBalance());
    };
  }, [dispatch]);

  useEffect(() => {
    if (trialBalanceFilters) {
      setStartDate(trialBalanceFilters.start_date || '');
      setEndDate(trialBalanceFilters.end_date || '');
      const includeFlag = trialBalanceFilters.include_zero_balance;
      setIncludeZero(
        includeFlag !== undefined &&
        includeFlag !== null &&
        (String(includeFlag) === '1' || String(includeFlag).toLowerCase() === 'true')
      );
    }
  }, [trialBalanceFilters]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = {
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      ...(includeZero ? { include_zero_balance: '1' } : includeZero === false ? { include_zero_balance: '0' } : {}),
    };
    dispatch(getTrialBalance(params));
  };

  const formattedTree = useMemo(() => {
    const data = trialBalance?.tree;
    return Array.isArray(data) ? data : [];
  }, [trialBalance]);

  const totals = trialBalance?.totals || null;
  const meta = trialBalance?.meta || null;

  const hasChildren = (node) => Array.isArray(node.children) && node.children.length > 0;

  const valueFormatter = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const cellStyle = {
    padding: '12px 10px',
    textAlign: 'center',
    color: '#f8fafc',
    fontSize: '14px',
    borderBottom: '1px solid #2f3b4f',
  };

  const footerCellStyle = {
    padding: '16px 12px',
    textAlign: 'center',
    color: '#f8fafc',
    fontSize: '15px',
    fontWeight: 700,
    borderTop: '2px solid #AC2000',
  };

  const buildDynamicPdfChunks = (items) => {
    const topHeader = document.getElementById('trial-balance-pdf-measure-top-header');
    const thead = document.getElementById('trial-balance-pdf-measure-thead');
    const footer = document.getElementById('trial-balance-pdf-measure-footer');
    const tbody = document.getElementById('trial-balance-pdf-measure-tbody');
    return calculatePdfPagination(items, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
  };

  const handlePreviewAll = () => {
    if (!formattedTree || formattedTree.length === 0) {
      toast.error(t('accountingReports.noDataPreview', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    if (flatRows.length === 0) return;
    const chunks = buildDynamicPdfChunks(flatRows);
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!formattedTree || formattedTree.length === 0) {
      toast.error(t('accountingReports.noDataPrint', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsGeneratingPdf(true);
      if (flatRows.length === 0) return;
      const chunks = buildDynamicPdfChunks(flatRows);
      setPdfChunks(chunks);

      await waitForRender(200);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `trial-balance-pdf-all-${i}`),
        fileName: datedFileName('TrialBalance'),
        isRTL,
      });
    } catch (err) {
      toast.error(t('accountingReports.printError', { defaultValue: 'فشل إنشاء ملف PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const flattenTree = (nodes, depth = 0, acc = []) => {
    if (!Array.isArray(nodes)) return acc;
    nodes.forEach((node) => {
      acc.push({
        id: node.id ?? null,
        code: node.code || '—',
        name_ar: node.name_ar || '—',
        name_en: node.name_en || '—',
        name: isRTL ? (node.name_ar || node.name_en || '—') : (node.name_en || node.name_ar || '—'),
        level: node.level ?? depth,
        opening_debit: node.opening_debit ?? 0,
        opening_credit: node.opening_credit ?? 0,
        period_debit: node.period_debit ?? 0,
        period_credit: node.period_credit ?? 0,
        total_debit: node.total_debit ?? 0,
        total_credit: node.total_credit ?? 0,
        closing_debit: node.closing_debit ?? 0,
        closing_credit: node.closing_credit ?? 0,
        depth,
      });
      if (Array.isArray(node.children)) flattenTree(node.children, depth + 1, acc);
    });
    return acc;
  };

  const flatRows = useMemo(() => flattenTree(formattedTree), [formattedTree]);

  const reportStart = meta?.start_date || startDate || '—';
  const reportEnd = meta?.end_date || endDate || '—';

  const renderTrialBalancePage = (chunk, chunkIndex, isPreview = false) => (
    <div
      key={`${isPreview ? 'preview' : 'print'}-${chunkIndex}`}
      id={`${isPreview ? 'preview-' : ''}trial-balance-pdf-all-${chunkIndex}`}
      style={{
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        padding: '40px',
        boxSizing: 'border-box',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        ...(isPreview ? { boxShadow: '0 2px 14px rgba(0, 0, 0, 0.18)' } : {}),
      }}
    >
      <div
        style={{
          border: '5px solid #000',
          padding: '3px',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          minHeight: '1043px',
          height: '1043px',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            border: '2px solid #000',
            minHeight: '1033px',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'visible',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '95%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '145px' }}>
              <div style={{ marginTop: '6px', textAlign: 'center', fontWeight: '700', fontSize: '18px', direction: dir }}>
                {t('accountingReports.trialBalanceTitle', { defaultValue: 'ميزان المراجعة' })}
              </div>
              <div style={{ marginTop: '6px', fontSize: '12px', direction: dir, textAlign: 'center', fontWeight: 600 }}>
                {t('accountingReports.period', { defaultValue: 'الفترة' })}: {reportStart} - {reportEnd}
              </div>
              <div style={{ marginTop: '6px', marginBottom: '8px', fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: '10px', direction: dir }}>
                <span>{t('accountingReports.accountsCount', { count: flatRows.length, defaultValue: `Accounts count: ${flatRows.length}` })}</span>
                <span>{t('accountingReports.levelsUpTo', { maxLevel: Math.max(...flatRows.map((r) => Number(r.level || 0)), 0), defaultValue: `Levels: up to ${Math.max(...flatRows.map((r) => Number(r.level || 0)), 0)}` })}</span>
                <span>{t('accountingReports.pageOf', { current: chunkIndex + 1, total: pdfChunks.length, defaultValue: `Page ${chunkIndex + 1} of ${pdfChunks.length}` })}</span>
              </div>

              <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1, overflow: 'hidden' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '10px',
                    border: '1px solid #000',
                    tableLayout: 'fixed',
                    backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.8)' }}>
                      {columnsMeta.map((col, colIdx) => (
                        <th
                          key={col.label}
                          style={{
                            border: '1px solid #000',
                            padding: '6px 4px',
                            fontWeight: '700',
                            width: colIdx === 0 ? '8%' : colIdx === 1 ? '24%' : '8.5%',
                            textAlign: col.align === 'right' || col.align === 'left'
                              ? (isRTL ? (col.align === 'right' ? 'right' : 'left') : (col.align === 'right' ? 'left' : 'right'))
                              : 'center',
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((row, idx) => (
                      <tr key={`${row.code}-${chunkIndex}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{convertArabicNumbersToEnglish(row.code)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: isRTL ? 'right' : 'left' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{`${'  '.repeat(row.depth)}${row.name}`}</div>
                              {row.name_ar !== row.name_en && (
                                <div style={{ marginTop: '2px', fontSize: '9px', color: '#374151' }}>
                                  {isRTL ? row.name_en : row.name_ar}
                                </div>
                              )}
                            </div>
                            <span style={{ fontSize: '9px', whiteSpace: 'nowrap', color: '#4b5563' }}>
                              {t('accountingReports.levelPrefix', { level: row.level, defaultValue: `L${row.level}` })}
                            </span>
                          </div>
                        </td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.opening_debit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.opening_credit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.period_debit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.period_credit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.total_debit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.total_credit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.closing_debit)}</td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.closing_credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {totals && chunkIndex === pdfChunks.length - 1 && (
                    <tfoot>
                      <tr>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('accountingReports.total', { defaultValue: 'الإجمالي' })}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>--</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.opening_debit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.opening_credit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.period_debit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.period_credit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.total_debit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.total_credit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.closing_debit)}</th>
                        <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{valueFormatter(totals.closing_credit)}</th>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0,
              }}
            >
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleExportAllToExcel = () => {
    if (!formattedTree || formattedTree.length === 0) {
      toast.error(t('accountingReports.noDataExport', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }
    try {
      const flat = flattenTree(formattedTree);
      const periodLabel = `${reportStart} - ${reportEnd}`;
      const generatedAt = new Date().toLocaleString('en-GB');
      const data = flat.map((row) => ({
        [t('common.id', { defaultValue: 'ID' })]: row.id ?? '—',
        [t('accountingReports.level', { defaultValue: 'Level' })]: row.level ?? row.depth,
        [t('accountingReports.accountCode', { defaultValue: 'Account Code' })]: convertArabicNumbersToEnglish(row.code),
        [t('accountingReports.account', { defaultValue: 'الحساب' })]: `${'  '.repeat(row.depth)}${row.name}`,
        [t('accountingReports.accountNameAr', { defaultValue: 'Account Name (AR)' })]: row.name_ar || '—',
        [t('accountingReports.accountNameEn', { defaultValue: 'Account Name (EN)' })]: row.name_en || '—',
        [t('trialBalance.openingDebit', { defaultValue: 'رصيد افتتاحي مدين' })]: valueFormatter(row.opening_debit),
        [t('trialBalance.openingCredit', { defaultValue: 'رصيد افتتاحي دائن' })]: valueFormatter(row.opening_credit),
        [t('trialBalance.periodDebit', { defaultValue: 'حركة الفترة مدين' })]: valueFormatter(row.period_debit),
        [t('trialBalance.periodCredit', { defaultValue: 'حركة الفترة دائن' })]: valueFormatter(row.period_credit),
        [t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })]: valueFormatter(row.total_debit),
        [t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })]: valueFormatter(row.total_credit),
        [t('trialBalance.closingDebit', { defaultValue: 'رصيد ختامي مدين' })]: valueFormatter(row.closing_debit),
        [t('trialBalance.closingCredit', { defaultValue: 'رصيد ختامي دائن' })]: valueFormatter(row.closing_credit),
        [t('accountingReports.period', { defaultValue: 'الفترة' })]: periodLabel,
        [t('accountingReports.generatedAt', { defaultValue: 'Generated At' })]: generatedAt,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [
        { wch: 10 },  // ID
        { wch: 8 },   // Level
        { wch: 18 },  // Code
        { wch: 42 },  // Account
        { wch: 34 },  // Name AR
        { wch: 34 },  // Name EN
        { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
        { wch: 24 },  // Period
        { wch: 22 },  // Generated At
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('accountingReports.trialBalanceTitle', { defaultValue: 'Trial Balance' }));
      XLSX.writeFile(wb, `TrialBalance_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (err) {
      toast.error(t('accountingReports.exportError', { defaultValue: 'فشل تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const columnsMeta = [
    { label: t('accountingReports.code', { defaultValue: 'كود الحساب' }), minWidth: '130px', align: 'center' },
    { label: t('accountingReports.account', { defaultValue: 'الحساب' }), minWidth: '220px', align: isRTL ? 'right' : 'left' },
    { label: t('accountingReports.openingDebit'), minWidth: '170px', align: 'center' },
    { label: t('accountingReports.openingCredit'), minWidth: '170px', align: 'center' },
    { label: t('accountingReports.periodDebit'), minWidth: '160px', align: 'center' },
    { label: t('accountingReports.periodCredit'), minWidth: '160px', align: 'center' },
    { label: t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' }), minWidth: '150px', align: 'center' },
    { label: t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' }), minWidth: '150px', align: 'center' },
    { label: t('accountingReports.closingDebit'), minWidth: '160px', align: 'center' },
    { label: t('accountingReports.closingCredit'), minWidth: '160px', align: 'center' },
  ];

  const renderRows = (nodes, depth = 0, parentKey = 'root') => {
    if (!Array.isArray(nodes)) return null;
    return nodes.map((node, index) => {
      const nodeId =
        node.id !== undefined && node.id !== null
          ? String(node.id)
          : `${parentKey}-${index}`;
      const children = hasChildren(node);
      const rowBg = depth % 2 === 0 ? '#1c2435' : '#202b3b';

      return (
        <React.Fragment key={`${nodeId}-${depth}`}>
          <tr style={{ backgroundColor: rowBg }}>
            <td style={{ ...cellStyle, minWidth: columnsMeta[0].minWidth }}>
              {convertArabicNumbersToEnglish(node.code || '---')}
            </td>
            <td
              style={{
                ...cellStyle,
                textAlign,
                minWidth: columnsMeta[1].minWidth,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', direction: dir }}>
                <span style={{ width: depth * 18 }} />
                {children ? (
                  <FaChevronDown style={{ color: '#AC2000' }} />
                ) : (
                  <span style={{ width: 16 }} />
                )}
                <span>{isRTL ? (node.name_ar || node.name_en || '---') : (node.name_en || node.name_ar || '---')}</span>
              </div>
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[2].minWidth }}>
              {valueFormatter(node.opening_debit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[3].minWidth }}>
              {valueFormatter(node.opening_credit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[4].minWidth }}>
              {valueFormatter(node.period_debit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[5].minWidth }}>
              {valueFormatter(node.period_credit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[6].minWidth }}>
              {valueFormatter(node.total_debit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[7].minWidth }}>
              {valueFormatter(node.total_credit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[8].minWidth }}>
              {valueFormatter(node.closing_debit)}
            </td>
            <td style={{ ...cellStyle, minWidth: columnsMeta[9].minWidth }}>
              {valueFormatter(node.closing_credit)}
            </td>
          </tr>
          {children && renderRows(node.children, depth + 1, nodeId)}
        </React.Fragment>
      );
    });
  };


  return (
    <div
      style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px',
          marginBottom: '24px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, direction: dir, textAlign }}>
            {t('accountingReports.trialBalanceTitle', { defaultValue: 'ميزان المراجعة' })}
          </h1>
          {meta && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#cbd5f5', direction: dir, textAlign }}>
              {t('accountingReports.period', { defaultValue: 'الفترة' })}: {meta.start_date || '---'} {t('accountingReports.to', { defaultValue: 'to' })} {meta.end_date || '---'}
            </div>
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          display: 'grid',
          gap: '16px',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', direction: dir, textAlign }}>
              {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', direction: dir, textAlign }}>
              {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '18px',
              flexWrap: 'wrap',
            }}
          >
            <input
              id="includeZero"
              type="checkbox"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <label htmlFor="includeZero" style={{ fontSize: '14px', direction: dir, textAlign }}>
              {t('accountingReports.showZeroBalances')}
            </label>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setIncludeZero(false);
              dispatch(clearTrialBalance());
            }}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('accountingReports.reset', { defaultValue: 'إعادة تعيين' })}
          </button>
          <button
            type="submit"
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('accountingReports.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
      </form>

      {/* Action Buttons */}
      {formattedTree && formattedTree.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center'
            }}
          >
            {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
          </button>
          {/* Print All button removed as requested */}
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center'
            }}
          >
            {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel' })}
          </button>
        </div>
      )}

      <div
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          overflowX: 'auto',
          marginBottom: '24px',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            minWidth: '100%',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#AC2000', color: '#fff' }}>
              {columnsMeta.map((col) => (
                <th
                  key={col.label}
                  style={{
                    padding: '18px 16px',
                    textAlign: col.align === 'right' || col.align === 'left'
                      ? (isRTL ? (col.align === 'right' ? 'right' : 'left') : (col.align === 'right' ? 'left' : 'right'))
                      : 'center',
                    fontSize: '14px',
                    borderBottom: '1px solid #333',
                    minWidth: col.minWidth,
                    whiteSpace: col.align === 'right' ? 'nowrap' : 'normal',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {t('accountingReports.loading', { defaultValue: 'جاري التحميل...' })}
                </td>
              </tr>
            ) : formattedTree.length > 0 ? (
              renderRows(formattedTree)
            ) : (
              <tr>
                <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات لعرضها.' })}
                </td>
              </tr>
            )}
          </tbody>
          {totals && (
            <tfoot>
              <tr style={{ backgroundColor: '#1f2937' }}>
                <th style={footerCellStyle}>{t('accountingReports.total', { defaultValue: 'الإجمالي' })}</th>
                <th style={footerCellStyle}>--</th>
                <th style={footerCellStyle}>{valueFormatter(totals.opening_debit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.opening_credit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.period_debit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.period_credit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.total_debit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.total_credit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.closing_debit)}</th>
                <th style={footerCellStyle}>{valueFormatter(totals.closing_credit)}</th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Hidden print template */}
      {pdfChunks.length > 0 && (
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px' }} ref={pdfContainerRef}>
          {pdfChunks.map((chunk, chunkIndex) => renderTrialBalancePage(chunk.items, chunkIndex))}
        </div>
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && formattedTree && formattedTree.length > 0}
        title={t('accountingReports.trialBalanceTitle', { defaultValue: 'ميزان المراجعة' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks.map((chunk, chunkIndex) => renderTrialBalancePage(chunk.items, chunkIndex, true))}
        </div>
      </PdfPreviewModal>

      {/* Measurement template for dynamic PDF pagination */}
      {/* Measurement width = the print template's true inner content width:
          794 - 80 (page padding) - 16 (outer frame) - 40 (inner frame) = 658px.
          Measuring at full 794px makes columns wider, rows shorter, and the
          paginator overpacks pages — clipping the last row. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '658px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="trial-balance-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.trialBalanceTitle')}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{reportStart} - {reportEnd}</div>
          </div>
        </div>
        <table
          style={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            fontSize: '10px',
            border: '1px solid #000',
            fontFamily: '"Times New Roman", "Times", serif',
            fontWeight: 'bold',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <thead id="trial-balance-pdf-measure-thead">
            <tr>
              {columnsMeta.map((col, colIdx) => (
                <th
                  key={col.label}
                  style={{
                    border: '1px solid #000',
                    padding: '6px 4px',
                    fontWeight: '700',
                    width: colIdx === 0 ? '8%' : colIdx === 1 ? '24%' : '8.5%',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody id="trial-balance-pdf-measure-tbody">
            {flatRows.map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{convertArabicNumbersToEnglish(row.code)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: isRTL ? 'right' : 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{`${'  '.repeat(row.depth)}${row.name}`}</div>
                      {row.name_ar !== row.name_en && (
                        <div style={{ marginTop: '2px', fontSize: '9px', color: '#374151' }}>
                          {isRTL ? row.name_en : row.name_ar}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: '9px', whiteSpace: 'nowrap', color: '#4b5563' }}>
                      {t('accountingReports.levelPrefix', { level: row.level, defaultValue: `L${row.level}` })}
                    </span>
                  </div>
                </td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.opening_debit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.opening_credit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.period_debit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.period_credit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.total_debit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.total_credit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.closing_debit)}</td>
                <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center' }}>{valueFormatter(row.closing_credit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="trial-balance-pdf-measure-footer" style={{ height: '145px' }} />
      </div>
    </div>
  );
};

export default TrialBalance;
